import * as React from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import {ThemeProvider} from '@mui/material/styles';
import {
  GridCallbackDetails,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridSortModel,
  GridToolbar
} from '@mui/x-data-grid';
import {Warehouse} from '../../../../shared/CommonInterfaces';
import {flexeMuiTheme} from '../../../../shared/mui/default-mui-theme';
import {FlexeDataGrid} from '../../../../shared/mui/flexe-data-grid/FlexeDataGrid';
import InboundService from '../../../../shared/services/InboundService';
import {GetInboundsFilters, Inbound, InboundFilterKeys} from '../../InboundInterfaces';
import {InboundsGridColumnDefinition, InboundsGridContent} from './InboundsGridColumnDefinition';

interface InboundsTableProps {
  warehouse: Warehouse;
  activeFilters: Record<string, string>;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isShipper: boolean;
}

const DEFAULT_PAGE_SIZE = 50;

const InboundsTable: React.FC<InboundsTableProps> = (props) => {
  const inboundsService = new InboundService();
  const [totalCount, setTotalCount] = useState<number>(999);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<InboundsGridContent[]>([]);
  // cache continuation tokens for paginating backwards and forwards. index = page number
  // tokens.current[0] is for page 0 (null), tokens.current[1] is for page 1, etc.
  const continuationTokens = useRef<string[]>([null]);

  // Derive behavior from isShipper
  const detailHrefBase = props.isShipper ? '../s/dropoffs/container' : '../wh/dropoffs/container';
  const columns = InboundsGridColumnDefinition(detailHrefBase, props.warehouse);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE
  });
  const [errors, setErrors] = useState<string[]>([]);
  const prevWarehouseId = useRef(props.warehouse.id);

  // initial load
  useEffect(() => {
    const warehouseChanged = prevWarehouseId.current !== props.warehouse.id;

    if (warehouseChanged) {
      prevWarehouseId.current = props.warehouse.id;
      invalidateContinuationTokens();
      setPaginationModel((prev) => ({...prev, page: 0}));
    }
  }, [props.warehouse]);

  // Fetch data when filters or pagination change.
  useEffect(() => {
    const requestFilters = buildRequestFilters();
    getInbounds(requestFilters);
  }, [props.activeFilters, paginationModel]);

  const buildRequestFilters = (): GetInboundsFilters => {
    const apiFilters: Partial<GetInboundsFilters> = {};

    for (const [key, value] of Object.entries(props.activeFilters)) {
      if (!value) continue;

      switch (key) {
        case InboundFilterKeys.PurchaseOrder:
          apiFilters['purchase-order[]'] = value.split(',').map((s) => s.trim());
          break;
        case InboundFilterKeys.InboundId: {
          const id = parseInt(value, 10);
          if (!isNaN(id)) {
            apiFilters['inbound-id[]'] = [id];
          }
          break;
        }
        case InboundFilterKeys.Sku:
          apiFilters['sku[]'] = value.split(',').map((s) => s.trim());
          break;
        case InboundFilterKeys.Status:
          apiFilters['state[]'] = [value];
          break;
        case InboundFilterKeys.TrailerNumber:
          apiFilters['trailer-number'] = value;
          break;
        case InboundFilterKeys.Vendor:
          apiFilters.vendor = value;
          break;
        case InboundFilterKeys.MoveDateStart:
          apiFilters['move-date-start'] = value;
          break;
        case InboundFilterKeys.MoveDateEnd:
          apiFilters['move-date-end'] = value;
          break;
        case InboundFilterKeys.ReservationId:
          apiFilters['reservation-id[]'] = [Number(value)];
          break;
      }
    }

    return {
      'warehouse-id': props.warehouse.id,
      'page-size': paginationModel.pageSize,
      'continuation-token': continuationTokens.current[paginationModel.page] || null,
      ...apiFilters
    };
  };

  const getInbounds = async (requestFilters: GetInboundsFilters) => {
    setErrors([]);
    setLoading(true);

    try {
      const response = await inboundsService.getInbounds(requestFilters);

      if (response?.data?.inbounds?.length > 0) {
        // cache the continuation for the next page
        if (response.data.continuationToken) {
          continuationTokens.current[paginationModel.page + 1] = response.data.continuationToken;
        }

        const parsedInbounds = response.data.inbounds
          .sort((inbound1, inbound2) => inbound2.id - inbound1.id)
          .map((apiInbound: Inbound) => deserializeInbound(apiInbound));
        setRows(parsedInbounds);
        setTotalCount(response.data.totalCount);
      } else if (response?.errors?.length > 0) {
        setErrors(response.errors.map((error) => error.detail));
        setRows([]);
        setTotalCount(0);
      } else {
        setErrors(['Zero Inbounds found']);
        setRows([]);
        setTotalCount(0);
      }
    } catch (e) {
      setErrors([e.toString()]);
    } finally {
      setLoading(false);
    }
  };

  const invalidateContinuationTokens = () => {
    continuationTokens.current = [null];
  };

  const deserializeInbound = (inbound: Inbound): InboundsGridContent => {
    return {
      id: inbound.id,
      poNumbers: inbound.purchaseOrders,
      status: inbound.state,
      trailerNumber: inbound.trailerNumber,
      vendor: inbound.vendor,
      sealNumber: inbound.sealNumber,
      estimatedDelivery: inbound.expectedArrivalDate,
      reservation: {
        id: inbound.reservation.id,
        shipper: inbound.reservation.shipperName
      }
    };
  };

  const onSortModelChange = (params: GridSortModel, _: GridCallbackDetails) => {
    // no sorting supported
    if (params.length === 0) {
      return;
    }
  };

  const handlePageChange = (newPaginationModel: GridPaginationModel, _: GridCallbackDetails) => {
    setPaginationModel(newPaginationModel);
  };

  const isOptionalColumnVisible = useMemo<GridColumnVisibilityModel>(() => {
    const hasTrailerNumberData = rows.some((row) => !!row.trailerNumber);
    const hasVendorData = rows.some((row) => !!row.vendor);
    const hasSealData = rows.some((row) => !!row.sealNumber);

    return {
      trailerNumber: hasTrailerNumberData,
      vendor: hasVendorData,
      sealNumber: hasSealData
    };
  }, [rows]);

  const errorDisplay = errors.map((error, idx) => {
    return (
      <div className="alert alert-danger" role="alert" key={`error:${idx}`}>
        {error}
      </div>
    );
  });

  function CustomToolbar() {
    return (
      <GridToolbar
        // Exclude the quick filter component
        slotProps={{
          quickFilter: {disabled: true}
        }}
      />
    );
  }

  return (
    <ThemeProvider theme={flexeMuiTheme}>
      {errorDisplay}
      <FlexeDataGrid
        autoHeight
        columns={columns}
        rows={rows}
        rowCount={totalCount}
        loading={isLoading}
        pageSizeOptions={[20, 50, 100]} // max is 100
        paginationMode={'server'}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        onSortModelChange={onSortModelChange}
        columnVisibilityModel={isOptionalColumnVisible}
        getRowHeight={() => 'auto'}
        sx={{
          'border': 'none',
          // adds padding for each row
          '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {py: '16px'}
        }}
        slots={{toolbar: CustomToolbar}}
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        disableRowSelectionOnClick
      />
    </ThemeProvider>
  );
};

export default InboundsTable;
